import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce, isEqual } from 'lodash';
import { ContentZonesService, WindowProxy } from '@sm360/widget-commons-core-services';
import { MEDIA_QUERIES } from '@sm360/widget-commons-general';
import { Quote, Vehicle } from '@sm360/widget-commons-vehicle';
import WIDGET_VERSION from 'trade-in-360/constants/widgetVersions.js';

import TradeInVehicleMapperService from 'trade-in-360/services/TradeInVehicleMapperService/TradeInVehicleMapperService';
import InstantEstimateIcon from 'trade-in-360/components/InstantEstimate/InstantEstimateIcon.jsx';
import NewInstantEstimateAutocomplete from 'trade-in-360/components/InstantEstimate/NewInstantEstimateAutocomplete.jsx';
import InstantEstimateModal from 'trade-in-360/components/InstantEstimateModal/InstantEstimateModal.jsx';
import InstantEstimateWrapper from 'trade-in-360/components/InstantEstimateWrapper/InstantEstimateWrapper.jsx';

import 'trade-in-360/components/InstantEstimate/InstantEstimate.scss';
import NewTradeInContext from '../../NewTradeInContext';

class InstantEstimate extends React.Component {
    // eslint-disable-next-line react/sort-comp
    static contextType = NewTradeInContext;

    constructor(props) {
        super(props);
        this.state = {
            isEstimateTradeInModalOpen: false,
            tradeInVehicle: {},
            inputValue: '',
            componentWasMounted: false,
            quote: this.props.quote,
        };
        this.onValueSelected = this.onValueSelected.bind(this);
        this.getParentSize = this.getParentSize.bind(this);
        this.getClassNameFromParentSize = this.getClassNameFromParentSize.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleTradeInSuccess = this.handleTradeInSuccess.bind(this);
        this.handleResize = debounce(() => this.forceUpdate(), 150).bind(this);
    }

    componentWillMount() {
        const windowProxy = WindowProxy.get();
        if (windowProxy) {
            windowProxy.addEventListener('resize', this.handleResize);
        }
    }

    componentDidMount() {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ componentWasMounted: true });
    }

    componentWillReceiveProps(newProps) {
        if (!isEqual(newProps.quote, this.props.quote)) {
            this.setState({
                quote: newProps.quote,
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Only use Redux accountTradeInVehicle for official evaluation flow
        if (
            (this.context.fieldsLocked ||
                this.context.isTradeInAppointment ||
                this.context.isOfficialTradeIn ||
                this.context.isViewOnly) &&
            (prevProps.accountTradeInVehicle.id !== this.props.accountTradeInVehicle.id ||
                prevProps.accountTradeInVehicle.appointmentDate !== this.props.accountTradeInVehicle.appointmentDate)
        ) {
            // Convert TradeInVehicleModel to an object that InstantEstimateWrapper can understand
            const internalTradeIn = TradeInVehicleMapperService.tradeInVehicleToInternalTradeIn(
                this.props.accountTradeInVehicle
            );
            // Pass this accountTradeInVehicle as the initial tradeInVehicle to the widget
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ tradeInVehicle: internalTradeIn });
        }
    }

    componentWillUnmount() {
        const windowProxy = WindowProxy.get();
        if (windowProxy) {
            windowProxy.removeEventListener('resize', this.handleResize);
        }
    }

    onValueSelected(inputValue, selectedValue) {
        this.setState({
            inputValue,
            isEstimateTradeInModalOpen: !!selectedValue,
            tradeInVehicle: selectedValue || {},
        });
        // TODO: need 1 news params on the func. "handler" and need to setState : tradeInVehicle as selectedValue
    }

    getParentSize() {
        const windowProxy = WindowProxy.get();
        if (!windowProxy) {
            return null;
        }
        const parent = windowProxy.document.getElementById(this.props.parentElementId);
        if (!parent) {
            return null;
        }
        return parent.offsetWidth;
    }

    getClassNameFromParentSize() {
        const size = this.getParentSize();
        if (!size) {
            return '';
        }
        if (size <= MEDIA_QUERIES.MOBILE.MAX) {
            return 'instant-estimate-mobile';
        }
        if (size >= MEDIA_QUERIES.TABLET.MIN && size <= MEDIA_QUERIES.TABLET.MAX) {
            return 'instant-estimate-tablet';
        }
        return '';
    }

    handleClose() {
        this.setState({ isEstimateTradeInModalOpen: false });
        // When modal is closed, disable official mode
        this.context.setIsOfficialTradeIn(false);
    }

    // Update quote revision and call onTradeInSuccess
    handleTradeInSuccess() {
        const { quote } = this.state || {};
        if (quote) {
            const updatedQuote = this.props.quotes.find(({ id }) => id === quote?.id);
            if (updatedQuote) {
                this.setState(
                    (prevState) => ({
                        quote: prevState.quote.set('revision', updatedQuote.__revision || updatedQuote.quoteRevision),
                    }),
                    () => this.props.onTradeInSuccess(this.state.quote)
                );
            }
        }
    }

    render() {
        // If client doesn't pay for CBB and modal version is requested, return null component
        if (!this.context.useCBB && this.props.isModal) return null;
        const isTrade360 = this.context.version === WIDGET_VERSION.TRADE_360;
        const czGroup = 'widget.tradein.teaser';

        const styleBackgroundPrimary = this.props.isModal
            ? {
                  backgroundColor: ContentZonesService.getContentZone(czGroup, 'primary.background.color')
                      ? ContentZonesService.getContentZone(czGroup, 'primary.background.color').getValue()
                      : '#21232e',
              }
            : {};
        const stylePatternOverlay = {
            backgroundImage:
                ContentZonesService.getContentZone(czGroup, 'pattern') &&
                `url(${ContentZonesService.getContentZone(czGroup, 'pattern').getValue()})`,
        };

        // important: classNames between server render and client render must match at first
        // for react hydration to work properly
        let classNames = 'instant-estimate__showroom';

        if (!this.props.isModal) {
            classNames = `${classNames} instant-estimate__showroom-embedded`.trim();
        }

        if (this.state.componentWasMounted) {
            classNames = `${classNames} ${this.getClassNameFromParentSize()}`.trim();
        }

        return (
            <div className={classNames} style={styleBackgroundPrimary}>
                {this.props.isModal ? (
                    <>
                        <div className="new-instant-estimate__wrapper instant-estimate__wrapper">
                            <InstantEstimateIcon />

                            <NewInstantEstimateAutocomplete
                                onAutoCompleteSelect={this.onValueSelected}
                                selectedValue={this.state.inputValue}
                                parentClassName={this.getClassNameFromParentSize()}
                                config={this.props.config}
                                isBarWrapper={this.props.isBarWrapper}
                                isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                                quote={this.props.quote}
                            />

                            {stylePatternOverlay.backgroundImage && (
                                <div
                                    role="img"
                                    className="instant-estimate__pattern"
                                    style={stylePatternOverlay}
                                    aria-label="Instant Estimate"
                                />
                            )}
                        </div>
                        <InstantEstimateModal
                            openModal={this.state.isEstimateTradeInModalOpen}
                            onClose={this.handleClose}
                            tradeInVehicle={this.state.tradeInVehicle}
                            isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                            quote={this.props.quote}
                            isLoginModalOpen={this.context.isLoginModalOpen}
                            isTrade360={isTrade360}
                            onTradeInSuccess={this.props.onTradeInSuccess}
                            appointmentEnabled={this.props.appointmentEnabled}
                            config={this.props.config}
                            isMultiDealerTradeIn={this.props.isMultiDealerTradeIn}
                            websiteDomain={this.props.websiteDomain}
                            standAlone={this.props.standAlone}
                        />
                    </>
                ) : (
                    <div className="crm">
                        <div id="dialog-desc">
                            <InstantEstimateWrapper
                                tradeInVehicle={this.state.tradeInVehicle}
                                isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                                quote={this.state.quote}
                                isModal={false}
                                onTradeInSuccess={this.handleTradeInSuccess}
                                appointmentEnabled={this.props.appointmentEnabled}
                                config={this.props.config}
                                standAlone={this.props.standAlone}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

InstantEstimate.displayName = 'InstantEstimate';

InstantEstimate.defaultProps = {
    isModal: false,
    accountTradeInVehicle: null,
    config: {},
    isBarWrapper: false,
    isMultiDealerTradeIn: false,
    websiteDomain: '',
    standAlone: false,
};

InstantEstimate.propTypes = {
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    parentElementId: PropTypes.string.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
    isModal: PropTypes.bool,
    accountTradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel), // trade vehicle in garage (from Redux)
    onTradeInSuccess: PropTypes.func.isRequired,
    appointmentEnabled: PropTypes.bool.isRequired,
    config: PropTypes.object,
    isBarWrapper: PropTypes.bool,
    isMultiDealerTradeIn: PropTypes.bool,
    websiteDomain: PropTypes.string,
    standAlone: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
    return {
        accountTradeInVehicle: state.tradeInVehicle,
        quotes: state.quotes,
    };
}

export default connect(
    mapStateToProps,
    null
)(InstantEstimate);

export { InstantEstimate };
